import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, Button, Container, IconButton, Paper, Stack, Switch, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  TypeCategories,
  clearScrollReference,
  openGroup,
  saveFramework,
  selectControlCount,
  selectFilteredSelectedGroupControls,
  selectImplementationStatus,
  selectScrollReferenceId,
  selectSelectedFrameworkId,
  selectShowInapplicable,
  selectShowOnlyUnfinished,
  setScrollPosition,
  toggleInapplicable,
  toggleOnlyUnfinished,
} from '../../redux/reducers/frameworkReducer';
import debounce from '../../utils/debounce';
import OperationalControl from './OperationalControl';
import OperationalControlList from './OperationalControlList';

function ImplementationStatusIndicators() {
  const { t } = useTranslation();
  const controlCount = useSelector((state) => selectControlCount(state, TypeCategories.OPERATIONAL));
  const implementationCount = useSelector((state) => selectImplementationStatus(state, TypeCategories.OPERATIONAL));
  return (
    <Box sx={{ mt: '16px', mb: '24px' }}>
      <Stack justifyContent="space-evenly" direction="row" spacing={2}>
        <Paper variant="elevation" subtype="completion" align="center" sx={{ width: '100%' }}>
          <Stack direction="column">
            <Typography variant="overline">{t('framework.controls.statusCount.notImplemented')}</Typography>
            <Typography variant="overline">{implementationCount.notImplemented}</Typography>
            <Typography variant="overline">
              {`${Math.round((implementationCount.notImplemented / controlCount) * 1000) / 10}%`}
            </Typography>
          </Stack>
        </Paper>
        <Paper variant="elevation" subtype="completion" align="center" sx={{ width: '100%' }}>
          <Stack direction="column">
            <Typography variant="overline">{t('framework.controls.statusCount.partiallyImplemented')}</Typography>
            <Typography variant="overline">{implementationCount.partiallyImplemented}</Typography>
            <Typography variant="overline">
              {`${Math.round((implementationCount.partiallyImplemented / controlCount) * 1000) / 10}%`}
            </Typography>
          </Stack>
        </Paper>
        <Paper variant="elevation" subtype="completion" align="center" sx={{ width: '100%' }}>
          <Stack direction="column">
            <Typography variant="overline">{t('framework.controls.statusCount.fullyImplemented')}</Typography>
            <Typography variant="overline">{implementationCount.fullyImplemented}</Typography>
            <Typography variant="overline">
              {`${Math.round((implementationCount.fullyImplemented / controlCount) * 1000) / 10}%`}
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}

function OperationalControls() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const frameworkId = useSelector(selectSelectedFrameworkId);
  const controlList = useSelector(selectFilteredSelectedGroupControls);
  const [editMode, setEditMode] = useState(false);
  const [showTitles, setShowTitles] = useState(true);
  const scrollReference = useRef(null);
  const scrollReferenceId = useSelector(selectScrollReferenceId);
  const showInapplicable = useSelector(selectShowInapplicable);
  const showOnlyUnfinished = useSelector(selectShowOnlyUnfinished);

  useEffect(() => {
    if (scrollReferenceId !== null) {
      scrollReference?.current?.scrollIntoView();
      dispatch(clearScrollReference());
      window.scrollTo({ top: 0 });
    }
  }, [scrollReferenceId]);

  useEffect(() => {
    dispatch(openGroup(TypeCategories.OPERATIONAL));
  }, []);

  const toggleEditMode = () => {
    if (editMode) {
      dispatch(saveFramework(frameworkId));
    }
    setEditMode(!editMode);
  };

  const toggleShowInapplicable = () => {
    dispatch(toggleInapplicable());
  };

  const toggleShowOnlyUnfinished = () => {
    dispatch(toggleOnlyUnfinished());
  };

  const toggleTitles = () => {
    setShowTitles(!showTitles);
  };

  const handleScroll = (e) => {
    dispatch(setScrollPosition(e.target.scrollTop));
  };
  const debouncedHandleScroll = debounce(handleScroll, 100);

  return (
    <Container variant="controlBaseView" disableGutters maxWidth={false}>
      {controlList.length === 0 ? (
        <Stack alignItems="center" sx={{ mt: '40px' }}>
          <Typography variant="noActivities">{t('framework.operational.noControlsSelected')}</Typography>
        </Stack>
      ) : (
        <>
          <Box sx={{ maxWidth: '1200px' }}>
            <ImplementationStatusIndicators />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="tabHeader" sx={{ ml: '16px' }}>
                {t('framework.tabs.operational')}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={showOnlyUnfinished} onChange={toggleShowOnlyUnfinished} />
                  <Typography>{t('framework.operational.showIncomplete')}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Switch checked={showInapplicable} onChange={toggleShowInapplicable} />
                  <Typography>{t('framework.operational.showInapplicable')}</Typography>
                </Stack>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={toggleEditMode}
                    endIcon={editMode ? <CheckIcon /> : <EditIcon />}
                  >
                    {editMode ? t('framework.controls.exitEditMode') : t('framework.controls.enterEditMode')}
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </Box>
          <Container variant="controlDataView" maxWidth={false} disableGutters>
            <Box
              sx={{
                p: '2px',
                mr: '16px',
                width: showTitles ? 400 : 44,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Paper variant="elevation" sx={{ minHeight: '100%' }} onClick={!showTitles ? () => toggleTitles() : null}>
                <Stack direction="row" justifyContent={showTitles ? 'flex-end' : 'center'}>
                  <IconButton onClick={showTitles ? () => toggleTitles() : null}>
                    {showTitles ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
                  </IconButton>
                </Stack>
                {showTitles && <OperationalControlList />}
              </Paper>
            </Box>
            <Box
              onScroll={debouncedHandleScroll}
              sx={{
                p: '2px',
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                maxWidth: showTitles ? 800 : 1140,
              }}
            >
              <Stack spacing={2} sx={{ width: '100%', marginBottom: '24px' }}>
                {controlList.map((control) => (
                  <OperationalControl
                    control={control}
                    key={control.id}
                    editMode={editMode}
                    scrollReference={scrollReference}
                  />
                ))}
              </Stack>
            </Box>
          </Container>
        </>
      )}
    </Container>
  );
}

export default OperationalControls;
